import WeeklyProgressLineChart from 'components/chart/WeeklyProgressLineChart';
import WeeklyProgressDiffLineChart from 'components/chart/WeeklyProgressDiffLineChart';
import { ComboBox } from 'components/selects/ComboBox';
import { SetupProject } from 'models/setup/SetupProject';
import { useEffect, useMemo, useState } from 'react';
import { Code } from 'models/common/CommonCode';
import { SetupProjectGraphCondition } from 'models/setup/home/SetupProjectGraph';
import { findProject } from 'apis/setup/SetupProject';
import { useTranslation } from 'react-i18next';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { CommonBG } from 'ui/theme/Color';
import styled from '@emotion/styled';
import {
  findSetupPrjSummGraph,
  findSetupPrjSummGraphAxis,
} from 'apis/setup/home/SetupProjectSummary';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import _ from 'lodash';
import { SetupProjectGraph } from 'models/setup/home/SetupProjectGraph';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import useSessionStore from 'stores/useSessionStore';

/**
 * @description 설비셋업관리 프로젝트별요약 > 주간 진행률
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  26.02.09    jihwan89       프로젝트명 콤보박스 개별 넓이 적용
 */

interface GraphItem {
  proc: string;
  procOrd: string;
  [x: string]: any;
}

const SetupProjectGraphPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const location = useLocation();
  const state = { ...location.state };
  const { elmPrjId } = useSessionStore();

  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [baseWeekData, setBaseWeekData] = useState<Code[]>([]);
  const [condition, setCondition] = useState<SetupProjectGraphCondition>({
    elmPrjId: state?.elmPrjId || elmPrjId || '',
    baseDate: moment().format('YYYYMMDD'),
  });
  const [graphData, setGraphData] = useState<SetupProjectGraph[]>([]);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.elmPrjId,
          cmnCdNm: cur.elmPrjNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjMasterData]);

  const xAxisData = useMemo(() => {
    return (
      baseWeekData?.map((o) => {
        const yymm = `${o.optValCtn3?.slice(4, 6)}/${o.optValCtn3?.slice(6, 8)}`;
        const week = (o.optValCtn6 || '') + 'W';
        return [yymm, week];
      }) || Array.from({ length: 13 }, () => null)
    );
  }, [baseWeekData]);

  const graphDataList: GraphItem[] = useMemo(() => {
    if (graphData?.length > 0) {
      const result = graphData.reduce((acc, item) => {
        const { tp, proc, procOrd, ...rest } = item;

        const dataArr = Object.keys(rest)
          .filter((key) => /^w\d+$/.test(key))
          .map((key) => (rest[key] == null ? null : Number(rest[key])));

        if (!acc[proc]) {
          acc[proc] = {
            proc: proc,
            procOrd: procOrd,
          } as GraphItem;
        }

        acc[proc][tp] = dataArr;
        return acc;
      }, {});
      return _.values(result);
    } else {
      return [];
    }
  }, [graphData]);

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const initCodes = async () => {
    openLoading(true);
    try {
      const prjList = await findProject({ searchVisibleSaveYn: 'N' });
      setPrjMasterData(prjList);
    } catch {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
          '요청 정보 조회 중 오류가 발생했습니다.'
        ),
      });
    }
    openLoading(false);
  };

  const getProjectGraph = async () => {
    if (!validate()) {
      return;
    }

    openLoading(true);
    findSetupPrjSummGraph(condition)
      .then((res) => {
        setGraphData(res?.map((o) => ({ ...o })) || []);
      })
      .finally(() => openLoading(false));
  };

  const getBaseWeek = async () => {
    const res = await findSetupPrjSummGraphAxis(condition);
    setBaseWeekData(res?.map((o) => ({ ...o })) || []);
  };

  const validate = () => {
    if (!condition?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트명을 선택하세요.', '프로젝트명을 선택하세요.'),
      });

      return false;
    } else if (!condition?.baseDate) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.기준일을 선택하세요.', '기준일을 선택하세요.'),
      });
      return false;
    }

    return true;
  };

  const onSearch = () => {
    getBaseWeek();
    getProjectGraph();
  };

  useEffect(() => {
    initCodes();
  }, []);

  useEffect(() => {
    // params로 넘어온 프로젝트 ID 값 세팅
    if (state?.elmPrjId && location.pathname === '/setup/home/project-graph') {
      setCondition((prev) => ({
        ...prev,
        elmPrjId: state.elmPrjId,
        baseDate: moment().format('YYYYMMDD'),
      }));
    }
  }, [location]);

  useEffect(() => {
    onSearch();
  }, [condition.elmPrjId]);

  return (
    <GraphContainer>
      <div className="userMetadata">
        <span>{t('setup.label.프로젝트명', '프로젝트명')}</span>
        <ComboBox
          style={{ width: '400px' }}
          placeholder={String(t('setup.label.선택', '선택'))}
          options={optionsPrjMaster}
          defaultValue={condition.elmPrjId}
          onChange={(value) => {
            handleOnChange('elmPrjId', value);
          }}
        />
        <span>{t('setup.label.기준일', '기준일')}</span>
        <CustomDatepicker
          isRange={false}
          minDate={null}
          value={condition.baseDate}
          onChange={(newValue) => {
            handleOnChange('baseDate', newValue?.toString().replaceAll('.', ''));
          }}
        />
        <SearchBoxButtons onSearch={onSearch} />
      </div>
      {graphDataList.map((item: GraphItem, idx) => {
        return (
          <GraphItem key={idx}>
            <section style={{ flex: 1, minWidth: 360 }}>
              <h2>{item?.proc}</h2>
              <WeeklyProgressLineChart
                weeks={xAxisData}
                plannedWeekly={item?.S1_PLAN_WK || []}
                actualWeekly={item?.S2_ATUL_WK || []}
                cumulativePlanned={item?.S4_PLAN_CUM || []}
                cumulativeActual={item?.S5_ATUL_CUM || []}
              />
            </section>
            <section style={{ flex: 1, minWidth: 360 }}>
              <h2>{t('setup.label.전주(실적 - 계획)', '전주(실적 - 계획)')}%</h2>
              <WeeklyProgressDiffLineChart
                weeks={xAxisData}
                plannedWeekly={item?.S1_PLAN_WK || []}
                actualWeekly={item?.S2_ATUL_WK || []}
              />
            </section>
          </GraphItem>
        );
      })}
      {graphDataList.length === 0 && (
        <div className="m-5 text-red-500 font-bold">
          셋업 일정이 등록되어야 주간 진행률을 볼 수 있습니다.
        </div>
      )}
    </GraphContainer>
  );
};

const GraphContainer = styled.div`
  height: calc(100vh - 160px);
  .userMetadata {
    display: flex;
    align-items: center;
    background: ${CommonBG.Basic};
    span {
      margin: 0 6px 0 16px;
      font-size: 14px;
      font-weight: 600;
    }
  }
`;

const GraphItem = styled.div`
  display: flex;
  gap: 16;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 10px;
  h2 {
    font-weight: bold;
  }
`;

export default SetupProjectGraphPage;
